import './DtcCommerceMmmRunPicker.scss';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetError } from '../../../../redux/modals';
import { MetricsLambdaFetch } from '../../../../utils/fetch-utils';
import { getCompanySlug } from '../../../../utils/hooks/useLocationCompany';
import { ModelRun } from '../../MmmRunSelector/mmm-run-selector-constants';
import { discoveryToModelRuns } from '../../MmmRunSelector/mmm-run-selector-adapters';
import { MmmRunSelector } from '../../MmmRunSelector/MmmRunSelector';
import { useDtcCommerceMmmData } from '../context/DtcCommerceMmmDataContext';
import { allowsClientFacingToggle } from '../dtc-commerce-mmm-variants';
import { applyClientFacingOverrides, runKey } from '../utils/dtc-commerce-mmm-runs-discovery';

// The same KPI-first run selector as /dtc-mmm and /commerce-mmm (DEV-23639), fed by this page's
// run discovery. External users only ever receive client-facing runs (discovery filters the rest).
export const DtcCommerceMmmRunPicker = () => {
  const { pathname } = useLocation();
  const client = getCompanySlug(pathname);
  const setError = useSetError();
  const { config, discovery, isInternal, selectedKpi, selectedTimestamp, selectRun } =
    useDtcCommerceMmmData();
  // Client-facing flips live here, not in the provider's discovery: changing discovery re-runs the
  // provider's data load and resets the date range for what is only a status change.
  const [clientFacingOverrides, setClientFacingOverrides] = useState<Record<string, boolean>>({});

  useEffect(() => setClientFacingOverrides({}), [discovery]);

  const kpiRuns = useMemo(
    () =>
      discoveryToModelRuns(
        applyClientFacingOverrides(discovery?.kpis ?? [], clientFacingOverrides)
      ),
    [discovery, clientFacingOverrides]
  );

  if (!selectedKpi) return undefined;

  // Same write as MmmVariantPage's toggleMMMRun: optimistic, errors surface in the error dialog.
  const toggleClientFacing = async (run: ModelRun, clientFacing: boolean) => {
    const key = runKey(run.kpi, run.refreshDate);
    const previous = clientFacingOverrides[key];
    setClientFacingOverrides(prev => ({ ...prev, [key]: clientFacing }));
    try {
      await MetricsLambdaFetch('/toggleMMMRun', {
        method: 'POST',
        body: {
          company: client,
          date: run.refreshDate,
          client_facing: clientFacing,
          branch: run.branch,
          kpi: run.kpi
        }
      });
    } catch (e) {
      // Restore the pre-click value so the menu doesn't keep showing a flip that never saved.
      setClientFacingOverrides(({ [key]: _failed, ...rest }) =>
        previous === undefined ? rest : { ...rest, [key]: previous }
      );
      setError({ message: e.message, reportError: e });
    }
  };

  return (
    <div className='dtc-commerce-mmm-run-picker'>
      <MmmRunSelector
        kpiRuns={kpiRuns}
        kpi={selectedKpi}
        selectedDate={selectedTimestamp ?? ''}
        isInternal={isInternal}
        menuAlign='end'
        onChange={({ kpi, run }) => selectRun(kpi, run.refreshDate)}
        onToggleClientFacing={allowsClientFacingToggle(config) ? toggleClientFacing : undefined}
      />
    </div>
  );
};
